/**
 * User contact helpers: E.164 conversion, stored-number parse, primary/secondary swap, edit-form prefill.
 */

import { useThemeStore } from '@/stores/themeStore';
import type { ContactKind, PrimaryContactKind } from '@/types/user.types';
import { formatPhoneByCountry, phoneFormats } from './phone-formats-utils';
import { getErrorMessage } from '@/utils/errors-utils';
import { COUNTRY_CODE_MAP, COUNTRY_NAME_TO_ISO } from '@/constants/phone-countries-catalog';

/** PATCH contact fields written by a primary (+ optional secondary) live input. */
export type PrimaryContactDtoFields = {
  email?: string;
  phone?: string;
  primaryContactKind?: PrimaryContactKind;
};

/** Converts a locally formatted phone number to E.164 for Firebase. */
export function phoneToE164(localPhone: string, isoCode: string = 'FR'): string {
  if (!localPhone?.trim()) return localPhone;

  const trimmed = localPhone.trim();

  // Already E.164 — just strip any stray spaces/formatting
  if (trimmed.startsWith('+')) {
    return '+' + trimmed.slice(1).replaceAll(/\D/g, '');
  }

  const format = phoneFormats[isoCode];
  const dialCode = format?.dialCode ?? '+33';

  const digitsOnly = trimmed.replaceAll(/\D/g, '');

  // True trunk (FR, RE, …) is dropped in E.164; significant leading 0 (CI, …) is kept.
  const trunk = format?.trunkPrefix ?? '';
  if (trunk && digitsOnly.startsWith(trunk)) {
    return dialCode + digitsOnly.slice(trunk.length);
  }

  return dialCode + digitsOnly;
}

/** True when the typed primary contact differs from the server email, phone, or kind. */
export function isPrimaryContactDirty(params: {
  mode: 'initial' | 'email' | 'phone';
  currentValue: string;
  storedEmail?: string | null;
  storedPhone?: string | null;
  serverKind?: PrimaryContactKind | null;
  defaultCountry: string;
}): boolean {
  if (params.mode === 'initial') return false;
  if (params.serverKind && params.mode !== params.serverKind) return true;
  if (params.mode === 'email') {
    return params.currentValue.trim() !== (params.storedEmail ?? '').trim();
  }
  const current = params.currentValue.trim();
  const stored = (params.storedPhone ?? '').trim();
  if (!current && !stored) return false;
  if (!current || !stored) return true;
  return phoneToE164(current, params.defaultCountry) !== phoneToE164(stored, params.defaultCountry);
}

/** Map a live/stored kind to the SmartContact mode used by dirty-check. */
export function toPrimaryContactMode(
  kind: string | null | undefined,
): 'initial' | 'email' | 'phone' {
  if (kind === 'email' || kind === 'phone') return kind;
  return 'initial';
}

/** Read the additive `phoneSetupLink` on a domain PATCH response. */
export function readPhoneSetupLink(data: unknown): string | undefined {
  if (!data || typeof data !== 'object') return undefined;
  const link = (data as { phoneSetupLink?: unknown }).phoneSetupLink;
  return typeof link === 'string' && link.length > 0 ? link : undefined;
}

/** Current-host phone-setup URL used when the PATCH omitted `phoneSetupLink`. */
export function buildPhoneSetupLoginPath(phone: string): string {
  return `/login?mode=phone-setup&phone=${encodeURIComponent(phone)}`;
}

/** Live primary (+ optional secondary) → email / phone / kind on a PATCH DTO. */
export function assignPrimaryContactFields(params: {
  dto: PrimaryContactDtoFields;
  primaryValue: string;
  primaryKind: 'email' | 'phone';
  primaryCountry?: string;
  secondaryValue?: string | null;
  secondaryCountry?: string;
  defaultCountry: string;
}): void {
  const country = params.primaryCountry || params.defaultCountry;
  params.dto.primaryContactKind = params.primaryKind;
  if (params.primaryKind === 'email') {
    params.dto.email = params.primaryValue.trim();
    const secondary = params.secondaryValue?.trim();
    if (secondary) {
      params.dto.phone = phoneToE164(secondary, params.secondaryCountry || params.defaultCountry);
    }
    return;
  }
  params.dto.phone = phoneToE164(params.primaryValue, country);
  const secondaryEmail = params.secondaryValue?.trim();
  if (secondaryEmail) params.dto.email = secondaryEmail;
}

/** Company-country label → ISO code, or null if unknown. */
function countryNameToIso(name: string | null | undefined): string | null {
  if (!name) return null;
  return COUNTRY_NAME_TO_ISO[name.toLowerCase().trim()] ?? null;
}

/** Dial digits without `+` → ISO; picker catalog wins on shared codes. */
function buildDialCodeToIso(): Record<string, string> {
  const fromFormats: Record<string, string> = {};
  for (const format of Object.values(phoneFormats)) {
    const digits = format.dialCode.slice(1);
    fromFormats[digits] ??= format.countryCode;
  }
  return { ...fromFormats, ...COUNTRY_CODE_MAP };
}

/** Combined picker + format dial map (ES/GB/US/… not in the web picker). */
const DIAL_CODE_TO_ISO = buildDialCodeToIso();

/** +262 NSN prefixes assigned to Mayotte (YT), not Réunion (RE). */
const MAYOTTE_NSN_PREFIXES = ['269', '639'] as const;

/** Réunion vs Mayotte ISO from a +262 national number. */
function reunionOrMayotteIso(digits: string): string {
  const nsn = digits.slice(3, 6);
  return MAYOTTE_NSN_PREFIXES.some((prefix) => nsn.startsWith(prefix)) ? 'YT' : 'RE';
}

/** Prefer the theme country when it shares this international dial code. */
function isoPreferringDefaultCountry(
  mappedIso: string,
  dialCode: string,
  defaultCountry?: string,
): string {
  if (defaultCountry && phoneFormats[defaultCountry]?.dialCode === `+${dialCode}`) {
    return defaultCountry;
  }
  return mappedIso;
}

/** Detect country from E.164 / international digits. Returns null if unknown. */
function detectCountryFromE164(
  digits: string,
  defaultCountry?: string,
): { dialCode: string; isoCode: string } | null {
  const dialCode = Object.keys(DIAL_CODE_TO_ISO)
    .sort((a, b) => b.length - a.length)
    .find((code) => digits.startsWith(code));
  if (!dialCode) return null;

  const mappedIso =
    dialCode === '262' && digits.length >= 4
      ? reunionOrMayotteIso(digits)
      : DIAL_CODE_TO_ISO[dialCode];

  return {
    dialCode,
    isoCode: isoPreferringDefaultCountry(mappedIso, dialCode, defaultCountry),
  };
}

/** Converts a stored phone (E.164, international, or local) to local display format. */
export function parseStoredPhone(
  raw: string,
  defaultCountry: string = 'FR',
): { displayValue: string; isoCode: string } {
  if (!raw?.trim()) {
    return { displayValue: '', isoCode: defaultCountry };
  }

  let cleaned = raw.trim();
  if (cleaned.startsWith('+')) {
    cleaned = '+' + cleaned.slice(1).replaceAll(/\D/g, '');
  } else {
    cleaned = cleaned.replaceAll(/\D/g, '');
  }

  let isoCode = defaultCountry;
  let localDigits = cleaned;

  if (cleaned.startsWith('+')) {
    const digitsOnly = cleaned.slice(1);
    const detected = detectCountryFromE164(digitsOnly, defaultCountry);

    if (detected) {
      isoCode = detected.isoCode;
      const significantDigits = digitsOnly.slice(detected.dialCode.length);
      const trunk = phoneFormats[isoCode]?.trunkPrefix ?? '';
      localDigits = trunk + significantDigits;
    } else {
      localDigits = digitsOnly;
    }
  } else if (cleaned.length > 10) {
    const detected = detectCountryFromE164(cleaned, defaultCountry);
    if (detected) {
      isoCode = detected.isoCode;
      const significantDigits = cleaned.slice(detected.dialCode.length);
      const trunk = phoneFormats[isoCode]?.trunkPrefix ?? '';
      localDigits = trunk + significantDigits;
    }
  }
  const displayValue = formatPhoneByCountry(localDigits, isoCode);

  return { displayValue, isoCode };
}

/** Stored phone → local display value + ISO country for edit forms. */
export function hydratePhoneFromApi(
  raw: string | null | undefined,
  defaultCountry: string = 'FR',
): { value: string; countryCode: string } {
  const parsed = parseStoredPhone(raw ?? '', defaultCountry);
  return { value: parsed.displayValue, countryCode: parsed.isoCode };
}

/** Stored kind wins; else infer from which of email/phone is present. */
export function resolvePrimaryContactKind(
  stored: PrimaryContactKind | null | undefined,
  email: string | null | undefined,
  phone: string | null | undefined,
): PrimaryContactKind | null {
  if (stored === 'email' || stored === 'phone') return stored;
  const hasEmail = Boolean(email?.trim());
  const hasPhone = Boolean(phone?.trim());
  if (hasEmail && !hasPhone) return 'email';
  if (hasPhone && !hasEmail) return 'phone';
  if (hasEmail && hasPhone) return 'email';
  return null;
}

/** Primary + secondary contact pair from kind / email / phone. */
export function resolveUserContacts(
  kind: PrimaryContactKind | null | undefined,
  email: string | null | undefined,
  phone: string | null | undefined,
): {
  kind: PrimaryContactKind | null;
  primary: { kind: PrimaryContactKind; value: string } | null;
  secondary: { kind: PrimaryContactKind; value: string } | null;
} {
  const resolved = resolvePrimaryContactKind(kind, email, phone);
  const emailValue = email?.trim() || '';
  const phoneValue = phone?.trim() || '';
  if (resolved === 'email') {
    return {
      kind: 'email',
      primary: emailValue ? { kind: 'email', value: emailValue } : null,
      secondary: phoneValue ? { kind: 'phone', value: phoneValue } : null,
    };
  }
  if (resolved === 'phone') {
    return {
      kind: 'phone',
      primary: phoneValue ? { kind: 'phone', value: phoneValue } : null,
      secondary: emailValue ? { kind: 'email', value: emailValue } : null,
    };
  }
  return { kind: null, primary: null, secondary: null };
}

/** `email` / `phone` only; other contact types become `undefined`. */
export function primaryContactKindFromType(
  contactType: string | undefined,
): PrimaryContactKind | undefined {
  return contactType === 'email' || contactType === 'phone' ? contactType : undefined;
}

/** Detail rows: principal first, then the other channel as secondary. */
export function rankedContactDetailFields(
  kind: PrimaryContactKind | null | undefined,
  email: string | null | undefined,
  phone: string | null | undefined,
): Array<{ iconKind: PrimaryContactKind; label: string; value: string }> {
  const resolved = resolveUserContacts(kind, email, phone);
  const fields: Array<{ iconKind: PrimaryContactKind; label: string; value: string }> = [];
  if (resolved.primary) {
    fields.push({
      iconKind: resolved.primary.kind,
      label: 'Contact principal',
      value: resolved.primary.value,
    });
  } else {
    fields.push({ iconKind: 'email', label: 'Contact principal', value: 'N/R' });
  }
  if (resolved.secondary) {
    fields.push({
      iconKind: resolved.secondary.kind,
      label: resolved.secondary.kind === 'phone' ? 'Téléphone (secondaire)' : 'Email (secondaire)',
      value: resolved.secondary.value,
    });
  }
  return fields;
}

/** Prefill primary/secondary contact fields for an edit form. */
export function hydrateUserContactFields(
  email: string | null | undefined,
  phone: string | null | undefined,
  defaultCountry: string,
  primaryContactKind?: PrimaryContactKind | null,
): {
  contact: string;
  contactType: 'email' | 'phone';
  contactCountryCode: string;
  secondaryContact: string;
  secondaryContactCountryCode: string;
} {
  const kind = resolvePrimaryContactKind(primaryContactKind, email, phone);
  if (kind === 'phone') {
    const primary = hydratePhoneFromApi(phone, defaultCountry);
    return {
      contact: primary.value,
      contactType: 'phone',
      contactCountryCode: primary.countryCode,
      secondaryContact: email?.trim() ?? '',
      secondaryContactCountryCode: defaultCountry,
    };
  }
  if (kind === 'email') {
    const secondary = hydratePhoneFromApi(phone, defaultCountry);
    return {
      contact: email?.trim() ?? '',
      contactType: 'email',
      contactCountryCode: defaultCountry,
      secondaryContact: secondary.value,
      secondaryContactCountryCode: secondary.countryCode,
    };
  }
  return {
    contact: '',
    contactType: 'email',
    contactCountryCode: defaultCountry,
    secondaryContact: '',
    secondaryContactCountryCode: defaultCountry,
  };
}

/** Stored email/phone → display mode + local phone / email value. */
export function resolveStoredContact(
  email: string | null,
  phone: string | null,
  primaryContactKind?: PrimaryContactKind | null,
): { mode: 'email' | 'phone'; displayValue: string; isoCode: string } {
  const kind = resolvePrimaryContactKind(primaryContactKind, email, phone);
  if (kind === 'phone') {
    const parsed = parseStoredPhone(phone ?? '');
    return {
      mode: 'phone',
      displayValue: parsed.displayValue,
      isoCode: parsed.isoCode,
    };
  }
  if (kind === 'email') {
    return {
      mode: 'email',
      displayValue: email?.trim() ?? '',
      isoCode: 'FR',
    };
  }
  return {
    mode: 'email',
    displayValue: '',
    isoCode: 'FR',
  };
}

/** Flip `email` ↔ `phone` (anything else becomes `email`). */
export function oppositeContactType(contactType: string | undefined): 'email' | 'phone' {
  return contactType === 'email' ? 'phone' : 'email';
}

/** True when primary is a real email/phone — swap is meaningful and secondary can show. */
export function canSwapPrimaryContact(contactType: string | undefined): boolean {
  return contactType === 'email' || contactType === 'phone';
}

/** Next contact fields after a type switch; clears secondary when type changes. */
export function nextContactFields(params: {
  previousType: string | undefined;
  value: string;
  type: string;
  countryCode: string | undefined;
  previousCountryCode?: string;
  defaultCountry: string;
  previousSecondary?: string;
  previousSecondaryCountry?: string;
}): {
  contact: string;
  contactType: string;
  contactCountryCode: string | undefined;
  secondaryContact: string;
  secondaryContactCountryCode: string;
} {
  const typeChanged =
    params.type !== 'unknown' &&
    params.previousType !== 'unknown' &&
    !!params.previousType &&
    params.type !== params.previousType;
  return {
    contact: params.value,
    contactType: params.type,
    contactCountryCode: params.countryCode || params.previousCountryCode,
    secondaryContact: typeChanged ? '' : (params.previousSecondary ?? ''),
    secondaryContactCountryCode: typeChanged
      ? params.defaultCountry
      : (params.previousSecondaryCountry ?? params.defaultCountry),
  };
}

/** Catalogued duplicate-contact error, or undefined when `exists` is not true. */
export function contactExistsErrorMessage(
  exists: boolean | null | undefined,
  contactType: string | undefined,
): string | undefined {
  if (exists !== true) return undefined;
  return getErrorMessage(contactType === 'phone' ? 'phoneAlreadyExists' : 'emailAlreadyExists');
}

/** True when both trimmed values are non-empty and equal. */
export function areContactsIdentical(
  primary: string | null | undefined,
  secondary: string | null | undefined,
): boolean {
  const left = primary?.trim() ?? '';
  const right = secondary?.trim() ?? '';
  return left !== '' && left === right;
}

/** Swap primary and secondary contact values and their country codes. */
function swapContacts(params: {
  primaryContact: string;
  primaryContactType: ContactKind;
  primaryCountryCode: string;
  secondaryContact: string;
  secondaryContactType: ContactKind;
  secondaryCountryCode: string;
}) {
  return {
    contact: params.secondaryContact,
    contactType: params.secondaryContactType,
    contactCountryCode: params.secondaryCountryCode,
    secondaryContact: params.primaryContact,
    secondaryContactCountryCode: params.primaryCountryCode,
  };
}

/**
 * Shape A — unified contact field: { contact, contactType, contactCountryCode, secondaryContact, secondaryContactCountryCode }.
 * Used by tutor, parent, student forms.
 */
export function applyContactSwapUnified<
  T extends {
    contact?: string;
    contactType?: string;
    contactCountryCode?: string;
    secondaryContact?: string;
    secondaryContactCountryCode?: string;
  },
>(formData: T, defaultCountry: string): T {
  const primaryType: ContactKind = formData.contactType === 'phone' ? 'phone' : 'email';
  const secondaryType: ContactKind = primaryType === 'email' ? 'phone' : 'email';
  const swapped = swapContacts({
    primaryContact: formData.contact ?? '',
    primaryContactType: primaryType,
    primaryCountryCode: formData.contactCountryCode ?? defaultCountry,
    secondaryContact: formData.secondaryContact ?? '',
    secondaryContactType: secondaryType,
    secondaryCountryCode: formData.secondaryContactCountryCode ?? defaultCountry,
  });
  return {
    ...formData,
    contact: swapped.contact,
    contactType: swapped.contactType,
    contactCountryCode: swapped.contactCountryCode,
    secondaryContact: swapped.secondaryContact,
    secondaryContactCountryCode: swapped.secondaryContactCountryCode,
  };
}

/** Apply the unified swap then write the five contact fields through `set`. */
export function writeUnifiedContactSwap(
  set: (field: string, value: unknown) => void,
  formData: Parameters<typeof applyContactSwapUnified>[0],
  defaultCountry: string,
) {
  const swapped = applyContactSwapUnified(formData, defaultCountry);
  set('contact', swapped.contact);
  set('contactType', swapped.contactType);
  set('contactCountryCode', swapped.contactCountryCode);
  set('secondaryContact', swapped.secondaryContact);
  set('secondaryContactCountryCode', swapped.secondaryContactCountryCode);
  return swapped;
}

/** Swap on split email/phone state (admin, volunteer, profile). */
function applyContactSwapSplit(params: {
  primaryContact: string;
  primaryContactType: PrimaryContactKind;
  primaryCountryCode: string;
  secondaryEmail: string;
  secondaryPhone: string;
  secondaryCountryCode: string;
}) {
  const secondaryValue =
    params.primaryContactType === 'email' ? params.secondaryPhone : params.secondaryEmail;
  const secondaryType: ContactKind = params.primaryContactType === 'email' ? 'phone' : 'email';
  const swapped = swapContacts({
    primaryContact: params.primaryContact,
    primaryContactType: params.primaryContactType,
    primaryCountryCode: params.primaryCountryCode,
    secondaryContact: secondaryValue,
    secondaryContactType: secondaryType,
    secondaryCountryCode: params.secondaryCountryCode,
  });
  const newPrimaryType: PrimaryContactKind = swapped.contactType === 'phone' ? 'phone' : 'email';
  const newSecondaryEmail = newPrimaryType === 'email' ? swapped.contact : swapped.secondaryContact;
  const newSecondaryPhone = newPrimaryType === 'phone' ? swapped.contact : swapped.secondaryContact;
  return {
    primaryContact: swapped.contact,
    primaryContactType: newPrimaryType,
    primaryCountryCode: swapped.contactCountryCode,
    secondaryEmail: newSecondaryEmail,
    secondaryPhone: newSecondaryPhone,
    secondaryCountryCode: swapped.secondaryContactCountryCode,
  };
}

/** Split swap: update primary state, patch email/phone on the form, re-validate contact. */
export function commitSplitContactSwap<
  T extends { email?: string; phone?: string; primaryContactKind?: PrimaryContactKind },
>(params: {
  primaryContact: string;
  primaryContactType: PrimaryContactKind;
  primaryCountryCode: string;
  formData: T;
  secondaryCountryCode: string;
  setPrimaryContact: (value: string) => void;
  setPrimaryContactType: (value: PrimaryContactKind) => void;
  setPrimaryContactCountryCode: (value: string) => void;
  setSecondaryCountryCode: (value: string) => void;
  validation: {
    validateField: (field: string, value: unknown, formData?: unknown) => unknown;
    markAsTouched: (field: string) => void;
    markAsDirty: (field: string) => void;
  };
}): T {
  const swapped = applyContactSwapSplit({
    primaryContact: params.primaryContact,
    primaryContactType: params.primaryContactType,
    primaryCountryCode: params.primaryCountryCode,
    secondaryEmail: params.formData.email ?? '',
    secondaryPhone: params.formData.phone ?? '',
    secondaryCountryCode: params.secondaryCountryCode,
  });
  params.setPrimaryContact(swapped.primaryContact);
  params.setPrimaryContactType(swapped.primaryContactType);
  params.setPrimaryContactCountryCode(swapped.primaryCountryCode);
  params.setSecondaryCountryCode(swapped.secondaryCountryCode);
  const updated = {
    ...params.formData,
    email: swapped.secondaryEmail,
    phone: swapped.secondaryPhone,
    primaryContactKind: swapped.primaryContactType,
  };
  params.validation.validateField('contact', swapped.primaryContact, {
    ...updated,
    _contactType: swapped.primaryContactType,
    _phoneCountryCode: swapped.primaryCountryCode,
    _primaryContactExists: undefined,
  });
  params.validation.markAsTouched('contact');
  params.validation.markAsDirty('contact');
  return updated;
}

/** Duplicate-contact or identical-contacts error for the primary field. */
export function primaryContactFieldError(
  exists: boolean | null | undefined,
  contactType: string | undefined,
  identical: boolean,
): string | undefined {
  const existsError = contactExistsErrorMessage(exists, contactType);
  if (existsError) return existsError;
  if (!identical) return undefined;
  return getErrorMessage('contactsIdentical');
}

/** Block submit while the primary contact exists, is checking, or matches secondary. */
export function isPrimaryContactSubmitBlocked(
  primaryExists: boolean | null | undefined,
  isPrimaryChecking: boolean,
  identical: boolean,
): boolean {
  return primaryExists === true || isPrimaryChecking || identical;
}

/** Form is valid when contacts differ, neither exists, exist-check is idle, and the rest of the form is valid. */
export function isPrimaryContactFormValid(params: {
  primaryExists: boolean | null | undefined;
  isPrimaryChecking: boolean;
  isSecondaryChecking?: boolean;
  secondaryExists?: boolean | null;
  identical: boolean;
  formValid?: boolean;
}): boolean {
  return (
    params.primaryExists !== true &&
    params.secondaryExists !== true &&
    !params.isPrimaryChecking &&
    !params.isSecondaryChecking &&
    !params.identical &&
    params.formValid !== false
  );
}

/** Radio value `'true'` / `'false'`; undefined for anything else. */
export function booleanRadioValue(value: unknown): string | undefined {
  if (value === true || value === 'true') return 'true';
  if (value === false || value === 'false') return 'false';
  return undefined;
}

/** true/'true' → true; anything else (including missing) → false. */
export function formBool(value: unknown): boolean {
  return value === true || value === 'true';
}

/** Theme company country → ISO, fallback `FR`. Phone / contact fields only. */
export function getDefaultCompanyCountryIso(): string {
  const iso = countryNameToIso(useThemeStore.getState().companyCountry);
  if (iso && phoneFormats[iso]) return iso;
  return 'FR';
}

/** Reactive default ISO for phone inputs (re-renders when company country changes). */
export function useDefaultPhoneCountry(): string {
  useThemeStore((state) => state.companyCountry);
  return getDefaultCompanyCountryIso();
}
